// frontend/src/modules/reports/pages/financial/PnlLitePage.tsx
//
// Phase 4 (Reports Frontend), Task 4. Monthly revenue/expenses/payroll/net
// table + a net trend line, per currency. Binding contract notes: payroll
// is operating-currency-only (footnote below); month buckets can fall
// OUTSIDE the picked range when a payroll run overlaps it — we render
// exactly what the backend returns, never clip client-side.

import * as React from 'react';
import { ReportShell, type ReportRange } from '../../components/ReportShell';
import { DataTable } from '../../../../design-system/components/DataTable';
import { WaveStockChart } from '../../../../design-system/charts';
import { VISUAL } from '../../../../design-system/visual';
import { formatMoney } from '../../../../design-system/format';
import { reportsApi, downloadReportCsv } from '../../api';
import type { PnlLiteReport } from '../../types';
import { useReportFetch } from './useReportFetch';
import { groupByCurrency } from './currencyGroup';

export function PnlLitePage() {
  const [range, setRange] = React.useState<ReportRange | null>(null);

  const fetcher = range ? () => reportsApi.pnlLite({ from: range.from, to: range.to }) : null;
  const result = useReportFetch<PnlLiteReport>(fetcher, [range?.from, range?.to]);
  const forbidden = result.status === 403;

  const report = result.data;
  const rows = report?.rows ?? [];
  const sections = groupByCurrency(rows);

  return (
    <ReportShell
      eyebrow="Reports / Financial"
      title="P&L (lite)"
      description="Revenue, expenses, payroll, and net — a simplified profit & loss view."
      onRangeChange={setRange}
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && (range === null || result.loading)}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={!forbidden && range !== null && !result.loading && !result.error && rows.length === 0}
      csv={range ? {
        filename: `pnl-lite-${range.from}-to-${range.to}.csv`,
        onDownload: () => downloadReportCsv(
          'pnl-lite',
          { from: range.from, to: range.to },
          `pnl-lite-${range.from}-to-${range.to}.csv`,
        ),
      } : null}
    >
      {report && (
        <div className="space-y-6">
          {sections.map(([currency, currencyRows]) => {
            const netSeries = currencyRows.map((r) => r.net);
            return (
              <div key={currency} className="space-y-4">
                <h3 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-[0.06em]">{currency}</h3>
                {netSeries.length >= 2 && (
                  <div className={[VISUAL.card, 'p-6'].join(' ')}>
                    <div className="text-[12px] font-medium text-muted-foreground mb-3">Net trend</div>
                    <WaveStockChart data={netSeries} height={100} filled />
                  </div>
                )}
                <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                  <DataTable
                    rows={currencyRows}
                    rowKey={(r) => `${r.bucket}-${currency}`}
                    columns={[
                      { key: 'bucket', header: 'Month', cell: (r) => r.bucket },
                      { key: 'revenue', header: 'Revenue', cell: (r) => formatMoney(r.revenue, currency) },
                      { key: 'expenses', header: 'Expenses', cell: (r) => formatMoney(r.expenses, currency) },
                      { key: 'payroll', header: 'Payroll', cell: (r) => formatMoney(r.payroll, currency) },
                      { key: 'net', header: 'Net', cell: (r) => <span className="font-semibold text-foreground">{formatMoney(r.net, currency)}</span> },
                    ]}
                  />
                </div>
              </div>
            );
          })}
          <p className="text-[12px] text-muted-foreground">Payroll is reported in your operating currency.</p>
          {/* A scaled payroll figure must never read as the run's real total —
              the service deliberately flags this rather than silently adjusting. */}
          {report?.payroll_prorated && (
            <p className="text-[12px] text-muted-foreground">
              Some payroll runs cross the edge of this date range, so their cost is counted only for
              the portion that falls inside it.
            </p>
          )}
        </div>
      )}
    </ReportShell>
  );
}
